/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Lightweight 3D scenes for the landing-page tiles. These are deliberately
// hand-rolled with plain spheres + cylinders (no Miew, no GalleryViewer) so
// the landing surface stays small and boots fast. They are NOT meant to be
// chemically faithful renderings — they are signposts. The real visualizers
// take over once the user picks a project type.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BufferGeometry,
  CatmullRomCurve3,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Object3D,
  TubeGeometry,
  Vector3,
} from 'three';
import { useFrame } from '@react-three/fiber';
import { Environment, Lightformer } from '@react-three/drei';
import { commonMolecules, targetProteins } from '../internalDatabase.ts';
import { ELEMENT_COLORS } from '../crystal/elementVisuals.ts';

// Resolve asset URLs from the internalDatabase registries rather than
// importing file paths directly — keeps a single source of truth so
// renaming or replacing a molecule/protein only touches internalDatabase.
// Note: these URLs are bundled static assets under the app's own origin
// (Vite copies the local .sdf / .pdb files into dist/assets/), so the
// runtime fetch is a same-origin read — never an external request.
// Fallbacks to empty string are fine: a missed lookup just leaves the
// tile blank, which the rest of the landing handles gracefully.
const buckyballUrl = (commonMolecules.find((m) => m.name === 'Buckminsterfullerene')?.url as string | undefined) ?? '';
const hivProteaseUrl = (targetProteins.find((p) => p.name === 'HIV-1 Protease')?.url as string | undefined) ?? '';
// Crambin (46 residues, single chain) is the smallest protein in the bundle —
// its CA trace makes a clean, fast-loading backbone ribbon for the Protein Lab
// tile, visually distinct from Drug Discovery's surface-plus-ligand render.
const crambinUrl = (targetProteins.find((p) => p.name === 'Crambin')?.url as string | undefined) ?? '';

const colorFor = (el: string): string => ELEMENT_COLORS[el] ?? '#cccccc';

interface AutoRotateProps {
  // Radians per second. Each tile picks its own so the four scenes don't
  // metronome together — independent phases read as "alive" rather than
  // "synchronized animation."
  speed?: number;
  axis?: 'x' | 'y' | 'xy';
  // Hover pauses rotation so the user can study a frozen frame. Resumes
  // from the same angle (no jump) when hover ends.
  paused?: boolean;
  children: React.ReactNode;
}

const AutoRotate = React.memo(({ speed = 0.25, axis = 'y', paused = false, children }: AutoRotateProps) => {
  const ref = useRef<Group>(null);
  useFrame((_, delta) => {
    if (paused) return;
    const g = ref.current;
    if (!g) return;
    if (axis === 'y' || axis === 'xy') g.rotation.y += speed * delta;
    if (axis === 'x' || axis === 'xy') g.rotation.x += speed * 0.4 * delta;
  });
  return <group ref={ref}>{children}</group>;
});

// Shared scene-component props. `paused` cascades to AutoRotate; `onReady`
// fires once the scene has its data and is meshable (used by the parent
// tile to dismiss the loading shimmer). Scenes that don't fetch fire it
// in a mount-time effect.
export interface SceneProps {
  paused?: boolean;
  onReady?: () => void;
}

interface AtomProps {
  position: [number, number, number];
  radius: number;
  color: string;
}

const Atom = React.memo(({ position, radius, color }: AtomProps) => (
  <mesh position={position}>
    <sphereGeometry args={[radius, 20, 16]} />
    <meshStandardMaterial color={color} roughness={0.35} metalness={0.05} />
  </mesh>
));

interface BondProps {
  from: [number, number, number];
  to: [number, number, number];
  radius?: number;
  // Ball-and-stick convention: each half of the bond takes the color of the
  // atom it touches. Pass both colors. Single-color bonds (e.g. C–C in C60
  // or Na–Cl lattice rods) pass the same value for both.
  fromColor: string;
  toColor: string;
}

// Ball-and-stick bond: two half-cylinders sharing ONE parent transform so
// they're guaranteed to align (no per-half lookAt drift). Both halves are
// openEnded — no inner caps to z-fight, no overlap region needed. Their
// walls meet exactly at y=0 with identical radius and theta divisions, so
// the join reads as a sharp color transition with no visible gap. The
// atom spheres at the outer ends fully cover the (also openEnded) outer
// caps. For same-color bonds (C–C in C60) the two halves are the same
// color, so the bond reads as a single continuous stick.
const Bond = React.memo(({ from, to, radius = 0.06, fromColor, toColor }: BondProps) => {
  const groupRef = useRef<Group>(null);
  const { center, halfLen, dir } = useMemo(() => {
    const a = new Vector3(...from);
    const b = new Vector3(...to);
    const len = a.distanceTo(b);
    const mid = a.clone().lerp(b, 0.5);
    const direction = b.clone().sub(a).normalize();
    return {
      center: mid.toArray() as [number, number, number],
      halfLen: len / 2,
      dir: direction,
    };
  }, [from, to]);

  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    // Local-space orientation: no `lookAt` (which would re-derive from
    // world coords and drift when the parent AutoRotate is already
    // spinning). +Y of the cylinder geometry → bond direction.
    g.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), dir);
  }, [dir]);

  return (
    <group ref={groupRef} position={center}>
      {/* Bottom half: from-color, centered at -halfLen/2 along +Y. */}
      <mesh position={[0, -halfLen / 2, 0]}>
        <cylinderGeometry args={[radius, radius, halfLen, 12, 1, true]} />
        <meshStandardMaterial color={fromColor} roughness={0.5} metalness={0.05} />
      </mesh>
      {/* Top half: to-color, centered at +halfLen/2 along +Y. */}
      <mesh position={[0, halfLen / 2, 0]}>
        <cylinderGeometry args={[radius, radius, halfLen, 12, 1, true]} />
        <meshStandardMaterial color={toColor} roughness={0.5} metalness={0.05} />
      </mesh>
    </group>
  );
});

// SDF V2000 → atom positions and bond pairs. Tiny parser — enough for the
// preset SDFs we ship for the landing tiles. We don't validate; if the file
// is malformed the tile renders empty and the rest of the landing still
// works.
interface ParsedSdf {
  atoms: { x: number; y: number; z: number; element: string }[];
  bonds: { a: number; b: number }[];
}

// van der Waals radii in Å for the elements we expect in proteins + common
// inhibitors. Used to size the Contact-Surface atom spheres. Fallback 1.7
// (carbon) for anything unrecognized — generic and visually close enough
// for a thumbnail.
const VDW_RADIUS: Record<string, number> = {
  H: 1.2,
  C: 1.7,
  N: 1.55,
  O: 1.52,
  F: 1.47,
  P: 1.8,
  S: 1.8,
  Cl: 1.75,
  Br: 1.85,
  I: 1.98,
};
const vdwFor = (el: string): number => VDW_RADIUS[el] ?? 1.7;

interface ProteinAtom {
  pos: [number, number, number];
  element: string;
}

// Extract heavy (non-H) ATOM records from a PDB, including the element so
// we can size atoms by their VdW radius for the Contact-Surface render.
// allowedChains restricts to the relevant subunits — for a homodimer we
// want BOTH chains so the binding cleft between them is captured. Alt-loc
// filter keeps alt-A (and the default blank) only, so disordered residues
// don't double-count.
const parseHeavyAtoms = (text: string, allowedChains: Set<string> = new Set(['A', ' '])): ProteinAtom[] => {
  const out: ProteinAtom[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith('ATOM')) continue;
    const altLoc = line.charAt(16);
    if (altLoc !== ' ' && altLoc !== 'A') continue;
    const chain = line.charAt(21);
    if (!allowedChains.has(chain)) continue;
    let element = line.substring(76, 78).trim();
    if (!element) {
      const atomName = line.substring(12, 16);
      element = atomName.replace(/^\s+/, '').charAt(0);
    }
    if (element === 'H') continue;
    const x = parseFloat(line.substring(30, 38));
    const y = parseFloat(line.substring(38, 46));
    const z = parseFloat(line.substring(46, 54));
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) out.push({ pos: [x, y, z], element });
  }
  return out;
};

// Extract a single bound ligand from a PDB's HETATM records by 3-letter
// residue code (e.g. "THK" for the HIV-1 protease inhibitor in 1AID).
// Drops hydrogens, picks alt-A, and skips waters / common buffer ions by
// requiring an explicit residue match.
interface LigandAtom {
  pos: [number, number, number];
  element: string;
}
const parseLigand = (text: string, residueCode: string): LigandAtom[] => {
  const out: LigandAtom[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith('HETATM')) continue;
    const altLoc = line.charAt(16);
    if (altLoc !== ' ' && altLoc !== 'A') continue;
    const resName = line.substring(17, 20).trim();
    if (resName !== residueCode) continue;
    let element = line.substring(76, 78).trim();
    if (!element) {
      // Old PDBs sometimes leave the element column blank; fall back to the
      // first non-digit, non-space character of the atom name.
      const atomName = line.substring(12, 16);
      element = atomName.replace(/^\s+|[\d\s]+$/g, '').charAt(0) || 'C';
    }
    if (element === 'H') continue;
    const x = parseFloat(line.substring(30, 38));
    const y = parseFloat(line.substring(38, 46));
    const z = parseFloat(line.substring(46, 54));
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) out.push({ pos: [x, y, z], element });
  }
  return out;
};

// Bonds for a small molecule from coordinates alone: any pair within ~1.85 Å
// (the upper bound of common heavy-atom single bonds, slightly inflated to
// catch slightly long C–N / C–O bonds in inhibitor structures). Good enough
// for thumbnail rendering — we don't need correct bond orders.
const inferLigandBonds = (atoms: LigandAtom[]): [number, number][] => {
  const out: [number, number][] = [];
  const cutoffSq = 1.85 * 1.85;
  for (let i = 0; i < atoms.length; i++) {
    for (let j = i + 1; j < atoms.length; j++) {
      const dx = atoms[i].pos[0] - atoms[j].pos[0];
      const dy = atoms[i].pos[1] - atoms[j].pos[1];
      const dz = atoms[i].pos[2] - atoms[j].pos[2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < cutoffSq && d2 > 0.4) out.push([i, j]);
    }
  }
  return out;
};

const parseSdfV2000 = (text: string): ParsedSdf => {
  const lines = text.split(/\r?\n/);
  // V2000 counts line is line 4 (0-indexed 3): "  60 90  0..." → atoms, bonds.
  const counts = lines[3];
  const nAtoms = parseInt(counts.substring(0, 3).trim(), 10);
  const nBonds = parseInt(counts.substring(3, 6).trim(), 10);
  const atoms: ParsedSdf['atoms'] = [];
  for (let i = 0; i < nAtoms; i++) {
    const ln = lines[4 + i];
    if (!ln) break;
    const x = parseFloat(ln.substring(0, 10));
    const y = parseFloat(ln.substring(10, 20));
    const z = parseFloat(ln.substring(20, 30));
    const element = ln.substring(31, 34).trim();
    atoms.push({ x, y, z, element });
  }
  const bonds: ParsedSdf['bonds'] = [];
  for (let i = 0; i < nBonds; i++) {
    const ln = lines[4 + nAtoms + i];
    if (!ln) break;
    const a = parseInt(ln.substring(0, 3).trim(), 10) - 1;
    const b = parseInt(ln.substring(3, 6).trim(), 10) - 1;
    if (Number.isFinite(a) && Number.isFinite(b)) bonds.push({ a, b });
  }
  return { atoms, bonds };
};

// --- C60 (Molecular Modeling) ---

export const C60Scene = React.memo(({ paused, onReady }: SceneProps) => {
  const [parsed, setParsed] = useState<ParsedSdf | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(buckyballUrl)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        setParsed(parseSdfV2000(text));
        onReady?.();
      })
      .catch(() => {
        /* tile just stays empty — landing still works */
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
  }, [onReady]);

  // Center + scale once the data lands so the buckyball fits the tile camera
  // independent of the SDF's natural coordinate magnitude.
  const { atomPositions, bondPairs } = useMemo(() => {
    if (!parsed) return { atomPositions: [], bondPairs: [] as ParsedSdf['bonds'] };
    const cx = parsed.atoms.reduce((s, a) => s + a.x, 0) / parsed.atoms.length;
    const cy = parsed.atoms.reduce((s, a) => s + a.y, 0) / parsed.atoms.length;
    const cz = parsed.atoms.reduce((s, a) => s + a.z, 0) / parsed.atoms.length;
    const positions = parsed.atoms.map((a) => [a.x - cx, a.y - cy, a.z - cz] as [number, number, number]);
    return { atomPositions: positions, bondPairs: parsed.bonds };
  }, [parsed]);

  return (
    <AutoRotate speed={0.35} axis="xy" paused={paused}>
      {atomPositions.map((p, i) => (
        <Atom key={i} position={p} radius={0.22} color={colorFor('C')} />
      ))}
      {bondPairs.map((b, i) => {
        const a = atomPositions[b.a];
        const c = atomPositions[b.b];
        if (!a || !c) return null;
        // C60 is all carbon — both halves take the C color, giving a uniform
        // gray cage without us special-casing single-color bonds.
        return <Bond key={i} from={a} to={c} radius={0.05} fromColor={colorFor('C')} toColor={colorFor('C')} />;
      })}
    </AutoRotate>
  );
});

// --- Docking (Drug Discovery) ---
// HIV-1 protease (PDB 1AID, chains A+B) rendered as a Contact Surface —
// every heavy atom is drawn as a sphere at its van der Waals radius, and
// the spheres' union forms the surface (the same "fused-spheres" silhouette
// you get from Miew's Contact Surface mode, just rendered via InstancedMesh
// rather than a meshed isosurface). The bound non-peptide inhibitor (HETATM
// residue THK from the same crystal structure) is shown as ball-and-stick
// INSIDE the active-site cleft. Real protein/real drug pair in the actual
// docked pose.

const HIV_PROTEASE_CHAINS = new Set(['A', 'B']);
const HIV_INHIBITOR_RESIDUE = 'THK';

// Renders N atom-spheres in one draw call via InstancedMesh. Positions and
// per-instance VdW scales come from `atoms`; one shared material. Sphere
// geometry kept low-res (12×10) because each instance is a small thumbnail
// dot anyway, and 2000 × ~120 tris is already ~240 k triangles.
const ContactSurfaceSpheres = React.memo(({ atoms, opacity }: { atoms: ProteinAtom[]; opacity: number }) => {
  const ref = useRef<InstancedMesh>(null);
  useEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const dummy = new Object3D();
    for (let i = 0; i < atoms.length; i++) {
      const a = atoms[i];
      dummy.position.set(a.pos[0], a.pos[1], a.pos[2]);
      dummy.scale.setScalar(vdwFor(a.element));
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.count = atoms.length;
    mesh.instanceMatrix.needsUpdate = true;
  }, [atoms]);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, atoms.length]} frustumCulled={false}>
      <sphereGeometry args={[1, 12, 10]} />
      <meshStandardMaterial color="#7aa6d6" roughness={0.4} metalness={0.05} transparent opacity={opacity} />
    </instancedMesh>
  );
});

export const DockingScene = React.memo(({ paused, onReady }: SceneProps) => {
  // Both the protein heavy atoms and the inhibitor come from the same PDB
  // file, so we fetch once and parse twice.
  const [parsed, setParsed] = useState<{
    protein: ProteinAtom[];
    ligand: LigandAtom[];
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(hivProteaseUrl)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        setParsed({
          protein: parseHeavyAtoms(text, HIV_PROTEASE_CHAINS),
          ligand: parseLigand(text, HIV_INHIBITOR_RESIDUE),
        });
        onReady?.();
      })
      .catch(() => {
        /* tile stays empty — landing still works */
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
  }, [onReady]);

  // Shared centroid + view scale so protein spheres AND ligand atoms land
  // in the same local frame — preserving the real docking pose. Atoms stay
  // in Å pre-scale and a single uniform scale on the wrapping group brings
  // the whole structure into the camera frame.
  const transform = useMemo(() => {
    if (!parsed || parsed.protein.length === 0) return null;
    const all = parsed.protein;
    const cx = all.reduce((s, a) => s + a.pos[0], 0) / all.length;
    const cy = all.reduce((s, a) => s + a.pos[1], 0) / all.length;
    const cz = all.reduce((s, a) => s + a.pos[2], 0) / all.length;
    let maxAbs = 0;
    for (const a of all) {
      const r = Math.max(Math.abs(a.pos[0] - cx), Math.abs(a.pos[1] - cy), Math.abs(a.pos[2] - cz));
      if (r > maxAbs) maxAbs = r;
    }
    // Target half-span ~1.7 view units (so structure fits the tile camera
    // at z=6 with fov=40, leaving a comfortable margin including the VdW
    // shell at the protein's edge).
    const viewScale = maxAbs > 0 ? 1.7 / maxAbs : 1;
    return { cx, cy, cz, viewScale };
  }, [parsed]);

  // Atoms (both protein and ligand) recentered on the protein centroid.
  // Kept in Å — the wrapping group below applies viewScale.
  const proteinCentered = useMemo(() => {
    if (!parsed || !transform) return null;
    return parsed.protein.map((a) => ({
      pos: [a.pos[0] - transform.cx, a.pos[1] - transform.cy, a.pos[2] - transform.cz] as [number, number, number],
      element: a.element,
    }));
  }, [parsed, transform]);

  const ligandView = useMemo(() => {
    if (!parsed || !transform) return null;
    const atoms = parsed.ligand.map(({ pos, element }) => ({
      pos: [pos[0] - transform.cx, pos[1] - transform.cy, pos[2] - transform.cz] as [number, number, number],
      element,
    }));
    const bonds = inferLigandBonds(parsed.ligand);
    return { atoms, bonds };
  }, [parsed, transform]);

  return (
    <AutoRotate speed={0.3} axis="y" paused={paused}>
      <group scale={transform?.viewScale ?? 1}>
        {proteinCentered && <ContactSurfaceSpheres atoms={proteinCentered} opacity={0.55} />}
        {ligandView?.atoms.map((a, i) => (
          // Ligand atoms sized at ~50% of VdW so they read distinctly as
          // ball-and-stick against the contact-surface background. Pre-scale
          // Å — the parent group applies viewScale.
          <Atom key={`l${i}`} position={a.pos} radius={vdwFor(a.element) * 0.5} color={colorFor(a.element)} />
        ))}
        {ligandView?.bonds.map(([i, j], k) => {
          const a = ligandView.atoms[i];
          const b = ligandView.atoms[j];
          return (
            <Bond
              key={`lb${k}`}
              from={a.pos}
              to={b.pos}
              radius={0.18}
              fromColor={colorFor(a.element)}
              toColor={colorFor(b.element)}
            />
          );
        })}
      </group>
    </AutoRotate>
  );
});

// --- Crystal Builder ---
// NaCl rock salt, 2×2×2 chunk. Coords are integer; we center on origin and
// scale to fit the camera.

export const CrystalScene = React.memo(({ paused, onReady }: SceneProps) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);
  const atoms = useMemo(() => {
    const out: { pos: [number, number, number]; el: string }[] = [];
    const N = 3; // 3×3×3 = 27 atoms — dense enough to read as "lattice"
    const a = 1.0;
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < N; j++) {
        for (let k = 0; k < N; k++) {
          const el = (i + j + k) % 2 === 0 ? 'Na' : 'Cl';
          out.push({ pos: [(i - (N - 1) / 2) * a, (j - (N - 1) / 2) * a, (k - (N - 1) / 2) * a], el });
        }
      }
    }
    return out;
  }, []);

  // Bonds between nearest-neighbor (Na–Cl) pairs only, to suggest the lattice.
  // Carry each end's element so the bond can be split-colored by atom species.
  const bonds = useMemo(() => {
    const out: { from: [number, number, number]; to: [number, number, number]; fromEl: string; toEl: string }[] = [];
    for (let i = 0; i < atoms.length; i++) {
      for (let j = i + 1; j < atoms.length; j++) {
        if (atoms[i].el === atoms[j].el) continue;
        const dx = atoms[i].pos[0] - atoms[j].pos[0];
        const dy = atoms[i].pos[1] - atoms[j].pos[1];
        const dz = atoms[i].pos[2] - atoms[j].pos[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 1.05) out.push({ from: atoms[i].pos, to: atoms[j].pos, fromEl: atoms[i].el, toEl: atoms[j].el });
      }
    }
    return out;
  }, [atoms]);

  return (
    <AutoRotate speed={0.28} axis="xy" paused={paused}>
      {atoms.map((a, i) => (
        <Atom key={i} position={a.pos} radius={a.el === 'Na' ? 0.28 : 0.36} color={colorFor(a.el)} />
      ))}
      {bonds.map((b, i) => (
        <Bond
          key={i}
          from={b.from}
          to={b.to}
          radius={0.035}
          fromColor={colorFor(b.fromEl)}
          toColor={colorFor(b.toEl)}
        />
      ))}
    </AutoRotate>
  );
});

// --- Semiconductor Design ---
// An E–k band-structure plot rather than a bare lattice (which read as a
// crystal): an upward conduction-band parabola and two downward valence-band
// parabolas (heavy + light hole) separated by the band gap. Mobile carriers
// sit at the band edges — glowing electrons in the conduction-band minimum,
// open "holes" at the valence-band maximum — and a vertical arrow across the
// gap suggests the optical transition. This mirrors what AIMS's tight-binding
// engine actually computes, and reads instantly as "electronic structure."

// One band as a smooth tube along a sampled E(k) parabola in the XY plane.
const BandTube = React.memo(
  ({ curve, color, radius = 0.05 }: { curve: CatmullRomCurve3; color: string; radius?: number }) => (
    <mesh>
      <tubeGeometry args={[curve, 48, radius, 8, false]} />
      <meshStandardMaterial color={color} roughness={0.4} metalness={0.1} />
    </mesh>
  ),
);

const CONDUCTION_COLOR = '#4ea1ff'; // electrons / conduction band
const VALENCE_COLOR = '#ff8a4e'; // holes / valence band

export const SemiconductorScene = React.memo(({ paused, onReady }: SceneProps) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const { conduction, heavyHole, lightHole, electrons, holes, gapArrow } = useMemo(() => {
    const kMin = -1.6;
    const kMax = 1.6;
    const gap = 0.95; // band-gap half-height is gap/2 about E = 0
    // Sample a parabola E(k) into a Catmull-Rom curve for a smooth tube.
    const sample = (fn: (k: number) => number): CatmullRomCurve3 => {
      const pts: Vector3[] = [];
      const N = 36;
      for (let i = 0; i <= N; i++) {
        const k = kMin + ((kMax - kMin) * i) / N;
        pts.push(new Vector3(k, fn(k), 0));
      }
      return new CatmullRomCurve3(pts);
    };
    const cMin = gap / 2; // conduction-band minimum
    const vMax = -gap / 2; // valence-band maximum
    const conductionCurve = sample((k) => cMin + 0.62 * k * k);
    const heavyHoleCurve = sample((k) => vMax - 0.52 * k * k);
    const lightHoleCurve = sample((k) => vMax - 0.28 * k * k);
    // Carriers cluster at the band edges (k ≈ 0): a couple of electrons just
    // inside the conduction minimum, a couple of holes at the valence top.
    const electronPos: [number, number, number][] = [
      [-0.32, cMin + 0.07, 0],
      [0.34, cMin + 0.08, 0],
      [0.02, cMin + 0.01, 0],
    ];
    const holePos: [number, number, number][] = [
      [-0.36, vMax - 0.07, 0],
      [0.33, vMax - 0.08, 0],
    ];
    // Vertical excitation arrow across the gap at k = 0.
    const arrow = { from: [0, vMax, 0] as [number, number, number], to: [0, cMin, 0] as [number, number, number] };
    return {
      conduction: conductionCurve,
      heavyHole: heavyHoleCurve,
      lightHole: lightHoleCurve,
      electrons: electronPos,
      holes: holePos,
      gapArrow: arrow,
    };
  }, []);

  // E spans roughly ±(gap/2 + 0.62·1.6²) ≈ ±1.6; target ~1.7 half-height.
  const scale = 1.05;

  return (
    // Rotate about Y so the planar E–k plot turns to show depth, reading as a
    // 3D chart rather than a flat sticker.
    <AutoRotate speed={0.3} axis="y" paused={paused}>
      <group scale={scale}>
        <BandTube curve={conduction} color={CONDUCTION_COLOR} radius={0.055} />
        <BandTube curve={heavyHole} color={VALENCE_COLOR} radius={0.055} />
        <BandTube curve={lightHole} color={VALENCE_COLOR} radius={0.04} />
        {/* Electrons — glowing filled spheres in the conduction band. */}
        {electrons.map((p, i) => (
          <mesh key={`e${i}`} position={p}>
            <sphereGeometry args={[0.13, 18, 14]} />
            <meshStandardMaterial
              color={CONDUCTION_COLOR}
              emissive={CONDUCTION_COLOR}
              emissiveIntensity={0.6}
              roughness={0.3}
            />
          </mesh>
        ))}
        {/* Holes — open/dim spheres at the valence-band maximum. */}
        {holes.map((p, i) => (
          <mesh key={`h${i}`} position={p}>
            <sphereGeometry args={[0.12, 18, 14]} />
            <meshStandardMaterial color="#2a1d14" emissive={VALENCE_COLOR} emissiveIntensity={0.18} roughness={0.6} />
          </mesh>
        ))}
        {/* Band-gap excitation arrow (valence max → conduction min at k = 0). */}
        <Bond
          from={gapArrow.from}
          to={gapArrow.to}
          radius={0.03}
          fromColor={VALENCE_COLOR}
          toColor={CONDUCTION_COLOR}
        />
      </group>
    </AutoRotate>
  );
});

// --- Polymer Builder ---
// Polyethylene-style chain — a short zigzag of backbone carbons, each with
// two hydrogens, to read instantly as "polymer chain." Geometry is hand-built
// (no SDF fetch) so the tile is mount-ready and avoids any external lookup.
// The backbone alternates above and below the X axis in the XY plane (the
// classic all-trans tetrahedral zigzag); hydrogens splay out of the plane
// (one +Z, one −Z) at the tetrahedral angle. Numbers are stylized for a
// clean silhouette — chemically suggestive rather than exact.

export const PolymerScene = React.memo(({ paused, onReady }: SceneProps) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const { carbons, hydrogens, ccBonds, chBonds } = useMemo(() => {
    // 10 backbone carbons — enough zigzag periods to read as "chain" but
    // short enough that each unit stays legible in the tile thumbnail.
    const nC = 10;
    // Projections of a tetrahedral C–C bond onto the zigzag plane.
    // dx ≈ 1.26 Å, dy ≈ 0.51 Å when the bond is 1.54 Å with HCH ~109.5°.
    // We use unitless figures here and rescale to fit the camera.
    const dx = 1.26;
    const dy = 0.51;
    const cs: [number, number, number][] = [];
    for (let i = 0; i < nC; i++) {
      const x = i * dx;
      const y = i % 2 === 0 ? +dy / 2 : -dy / 2;
      cs.push([x, y, 0]);
    }
    // C–C backbone bonds.
    const cc: [number, number][] = [];
    for (let i = 0; i < nC - 1; i++) cc.push([i, i + 1]);
    // Each interior carbon: 2 hydrogens, splayed +Z and −Z (out of the
    // zigzag plane), tilted slightly along ±Y to preserve the tetrahedral
    // look. Terminal carbons get an extra hydrogen along the chain axis to
    // cap the ends (CH3 endgroups — visually obvious "this chain ends here").
    const hs: [number, number, number][] = [];
    const ch: [number, number][] = []; // [carbonIndex, hydrogenIndex]
    const hOffOut = 0.9; // out-of-plane distance
    const hOffY = 0.45; // anti-correlate with the C's own y so H's point "outward"
    for (let i = 0; i < nC; i++) {
      const [cx, cy, cz] = cs[i];
      // sign so that hydrogens point away from the backbone kink, not into it
      const ySign = cy > 0 ? +1 : -1;
      // Two out-of-plane hydrogens on every carbon.
      const hIdxA = hs.length;
      hs.push([cx, cy + ySign * hOffY, cz + hOffOut]);
      ch.push([i, hIdxA]);
      const hIdxB = hs.length;
      hs.push([cx, cy + ySign * hOffY, cz - hOffOut]);
      ch.push([i, hIdxB]);
      // Endgroup hydrogen along the chain axis to make CH3 caps.
      if (i === 0) {
        const hIdxC = hs.length;
        hs.push([cx - dx * 0.8, cy - ySign * hOffY * 0.6, cz]);
        ch.push([i, hIdxC]);
      } else if (i === nC - 1) {
        const hIdxC = hs.length;
        hs.push([cx + dx * 0.8, cy - ySign * hOffY * 0.6, cz]);
        ch.push([i, hIdxC]);
      }
    }
    return { carbons: cs, hydrogens: hs, ccBonds: cc, chBonds: ch };
  }, []);

  // Center on the chain midpoint and scale so the whole chain comfortably
  // fits the tile camera. Half-span in x is ~(nC-1)*dx/2 ≈ 5.67; target a
  // total width of ~3.6 view units → scale ≈ 0.32. Centering keeps both
  // ends in frame and lets the slow Y-rotation feel anchored.
  const transform = useMemo(() => {
    if (carbons.length === 0) return { center: [0, 0, 0] as [number, number, number], scale: 1 };
    const cx = (carbons[0][0] + carbons[carbons.length - 1][0]) / 2;
    const halfSpan = (carbons[carbons.length - 1][0] - carbons[0][0]) / 2;
    const targetHalf = 1.8;
    const s = halfSpan > 0 ? targetHalf / halfSpan : 1;
    return { center: [cx, 0, 0] as [number, number, number], scale: s };
  }, [carbons]);

  const shift = (p: [number, number, number]): [number, number, number] => [
    p[0] - transform.center[0],
    p[1] - transform.center[1],
    p[2] - transform.center[2],
  ];

  return (
    <AutoRotate speed={0.3} axis="y" paused={paused}>
      <group scale={transform.scale}>
        {carbons.map((p, i) => (
          <Atom key={`c${i}`} position={shift(p)} radius={0.42} color={colorFor('C')} />
        ))}
        {hydrogens.map((p, i) => (
          <Atom key={`h${i}`} position={shift(p)} radius={0.22} color={colorFor('H')} />
        ))}
        {ccBonds.map(([a, b], i) => (
          <Bond
            key={`cc${i}`}
            from={shift(carbons[a])}
            to={shift(carbons[b])}
            radius={0.11}
            fromColor={colorFor('C')}
            toColor={colorFor('C')}
          />
        ))}
        {chBonds.map(([c, h], i) => (
          <Bond
            key={`ch${i}`}
            from={shift(carbons[c])}
            to={shift(hydrogens[h])}
            radius={0.08}
            fromColor={colorFor('C')}
            toColor={colorFor('H')}
          />
        ))}
      </group>
    </AutoRotate>
  );
});

// --- Protein Lab ---
// A secondary-structure cartoon — the classic "ribbons" representation: red
// helix ribbons, yellow β-sheet arrows, green coil tubes. Distinct from Drug
// Discovery's contact-surface-plus-ligand render. We read the per-residue
// secondary structure straight from the PDB's HELIX / SHEET records (Crambin
// ships them) and the ordered CA atoms, then build one flat ribbon / arrow /
// tube per contiguous secondary-structure run. Single chain matters: a
// multi-chain trace would leap spuriously between chain termini.

interface CaResidue {
  pos: [number, number, number];
  seq: number;
}

// Ordered CA atoms (chain A / blank, alt-loc A / blank) with their residue
// sequence numbers — order = file order = residue order for a single chain.
const parseCaResidues = (text: string): CaResidue[] => {
  const out: CaResidue[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith('ATOM')) continue;
    const altLoc = line.charAt(16);
    if (altLoc !== ' ' && altLoc !== 'A') continue;
    if (line.substring(12, 16).trim() !== 'CA') continue;
    const chain = line.charAt(21);
    if (chain !== 'A' && chain !== ' ') continue;
    const seq = parseInt(line.substring(22, 26).trim(), 10);
    const x = parseFloat(line.substring(30, 38));
    const y = parseFloat(line.substring(38, 46));
    const z = parseFloat(line.substring(46, 54));
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) out.push({ pos: [x, y, z], seq });
  }
  return out;
};

// Secondary-structure residue ranges from HELIX / SHEET records (chain A /
// blank only). Column offsets are the fixed PDB format: HELIX init/end seqNum
// at cols 22-25 / 34-37, SHEET init/end seqNum at cols 23-26 / 34-37.
interface SsRange {
  start: number;
  end: number;
  type: 'H' | 'E';
}
const parseSecondaryStructure = (text: string): SsRange[] => {
  const out: SsRange[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (line.startsWith('HELIX')) {
      if (line.charAt(19) !== 'A' && line.charAt(19) !== ' ') continue;
      const start = parseInt(line.substring(21, 25).trim(), 10);
      const end = parseInt(line.substring(33, 37).trim(), 10);
      if (Number.isFinite(start) && Number.isFinite(end)) out.push({ start, end, type: 'H' });
    } else if (line.startsWith('SHEET')) {
      if (line.charAt(21) !== 'A' && line.charAt(21) !== ' ') continue;
      const start = parseInt(line.substring(22, 26).trim(), 10);
      const end = parseInt(line.substring(33, 37).trim(), 10);
      if (Number.isFinite(start) && Number.isFinite(end)) out.push({ start, end, type: 'E' });
    }
  }
  return out;
};

// Flat ribbon strip along a CA spline. The strip spans the Frenet normal
// (width) and is lit by the binormal (its flat face), so a helix's twisting
// frame produces the curled-ribbon look. `arrow` tapers the last ~30% from a
// wide barb to a point — the β-strand arrowhead. Built as a two-sided strip
// (no thickness) since these are thumbnail cartoons, not solid extrusions.
const buildRibbon = (points: Vector3[], width: number, arrow: boolean): BufferGeometry => {
  const curve = new CatmullRomCurve3(points);
  const div = Math.max(12, (points.length - 1) * 10);
  const frames = curve.computeFrenetFrames(div, false);
  const pos: number[] = [];
  const nor: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= div; i++) {
    const t = i / div;
    const p = curve.getPointAt(t);
    const N = frames.normals[i];
    const B = frames.binormals[i];
    let halfW = width / 2;
    if (arrow) {
      const head = 0.7;
      // Wide barb at the head base (1.7× the body) tapering linearly to a point.
      if (t >= head) halfW = width * 0.85 * (1 - (t - head) / (1 - head));
    }
    pos.push(p.x + N.x * halfW, p.y + N.y * halfW, p.z + N.z * halfW);
    pos.push(p.x - N.x * halfW, p.y - N.y * halfW, p.z - N.z * halfW);
    nor.push(B.x, B.y, B.z, B.x, B.y, B.z);
  }
  for (let i = 0; i < div; i++) {
    const a = i * 2;
    idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
  }
  const geom = new BufferGeometry();
  geom.setAttribute('position', new Float32BufferAttribute(pos, 3));
  geom.setAttribute('normal', new Float32BufferAttribute(nor, 3));
  geom.setIndex(idx);
  return geom;
};

interface CartoonSegment {
  kind: 'H' | 'E' | 'C';
  geometry: BufferGeometry;
}

export const ProteinScene = React.memo(({ paused, onReady }: SceneProps) => {
  const [data, setData] = useState<{ residues: CaResidue[]; ss: SsRange[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(crambinUrl)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        setData({ residues: parseCaResidues(text), ss: parseSecondaryStructure(text) });
        onReady?.();
      })
      .catch(() => {
        /* tile stays empty — landing still works */
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
  }, [onReady]);

  // Center + scale the CA trace, classify each residue (H/E/C), then emit one
  // ribbon / arrow / tube per contiguous run. Runs overlap their neighbors by
  // one residue so consecutive segments meet without a gap.
  const segments = useMemo<CartoonSegment[] | null>(() => {
    if (!data || data.residues.length < 2) return null;
    const res = data.residues;
    const n = res.length;
    const cx = res.reduce((s, r) => s + r.pos[0], 0) / n;
    const cy = res.reduce((s, r) => s + r.pos[1], 0) / n;
    const cz = res.reduce((s, r) => s + r.pos[2], 0) / n;
    let maxAbs = 0;
    for (const r of res) {
      maxAbs = Math.max(maxAbs, Math.abs(r.pos[0] - cx), Math.abs(r.pos[1] - cy), Math.abs(r.pos[2] - cz));
    }
    // Leave headroom for the ribbon half-width that extends past the centerline.
    const viewScale = maxAbs > 0 ? 1.7 / maxAbs : 1;
    const pts = res.map(
      (r) => new Vector3((r.pos[0] - cx) * viewScale, (r.pos[1] - cy) * viewScale, (r.pos[2] - cz) * viewScale),
    );
    // Per-residue type from the SS ranges; default coil.
    const types: ('H' | 'E' | 'C')[] = res.map((r) => {
      for (const range of data.ss) if (r.seq >= range.start && r.seq <= range.end) return range.type;
      return 'C';
    });
    // Contiguous runs of equal type.
    const runs: { type: 'H' | 'E' | 'C'; a: number; b: number }[] = [];
    let s = 0;
    for (let i = 1; i <= n; i++) {
      if (i === n || types[i] !== types[s]) {
        runs.push({ type: types[s], a: s, b: i - 1 });
        s = i;
      }
    }
    const out: CartoonSegment[] = [];
    for (const run of runs) {
      const lo = Math.max(0, run.a - 1);
      const hi = Math.min(n - 1, run.b + 1);
      const segPts = pts.slice(lo, hi + 1);
      if (segPts.length < 2) continue;
      if (run.type === 'H') out.push({ kind: 'H', geometry: buildRibbon(segPts, 0.62, false) });
      else if (run.type === 'E') out.push({ kind: 'E', geometry: buildRibbon(segPts, 0.6, true) });
      else {
        const tube = new TubeGeometry(
          new CatmullRomCurve3(segPts),
          Math.max(8, (segPts.length - 1) * 8),
          0.08,
          8,
          false,
        );
        out.push({ kind: 'C', geometry: tube });
      }
    }
    return out;
  }, [data]);

  return (
    <>
      {/* A faint in-memory studio environment (NO HDR download) gives the flat
          ribbons just enough to reflect to read as a soft SHEEN — without it they
          go dead-matte. Kept subtle (low envMapIntensity below) to match the
          Crystal scene's gentle gloss rather than a mirror. Scoped to this scene
          via the tile's <View>. */}
      <Environment resolution={64} frames={1}>
        <Lightformer form="rect" intensity={3} position={[3, 3, 4]} scale={[6, 6, 1]} />
        <Lightformer form="rect" intensity={2} color="#cfe0ff" position={[-4, 2, 2]} scale={[5, 5, 1]} />
        <Lightformer form="ring" intensity={2} color="#ffe0c2" position={[0, -3, 3]} scale={[4, 4, 1]} />
      </Environment>
      <AutoRotate speed={0.3} axis="y" paused={paused}>
        {segments?.map((seg, i) => (
          <mesh key={i} geometry={seg.geometry}>
            <meshStandardMaterial
              // Soft gloss matching the Crystal Builder scene (roughness ~0.35,
              // low metalness): a gentle highlight, not a mirror. The faint
              // environment above gives the flat ribbons a subtle moving sheen;
              // a little emission keeps the colors vivid against the dark tile.
              color={seg.kind === 'H' ? '#ff4d4d' : seg.kind === 'E' ? '#ffe21f' : '#2fe853'}
              emissive={seg.kind === 'H' ? '#ff4d4d' : seg.kind === 'E' ? '#ffe21f' : '#2fe853'}
              emissiveIntensity={seg.kind === 'H' ? 0.2 : 0.14}
              roughness={0.55}
              metalness={0.03}
              envMapIntensity={0.22}
              // Helix / sheet ribbons are flat strips — render both faces.
              side={seg.kind === 'C' ? undefined : DoubleSide}
            />
          </mesh>
        ))}
      </AutoRotate>
    </>
  );
});

// --- DNA / RNA Lab ---
// A procedural B-form double helix: two sugar-phosphate backbones spiraling in
// antiphase with base-pair rungs between them. Fully hand-built (no fetch) so
// the tile is mount-ready. The twisted-ladder silhouette reads instantly as
// "nucleic acid," clearly distinct from the protein ribbon.

export const DnaScene = React.memo(({ paused, onReady }: SceneProps) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const { strandA, strandB, rungs } = useMemo(() => {
    const nbp = 13; // base pairs — ~1.25 helical turns at 34°/bp
    const rise = 0.42; // vertical rise per base pair
    const twist = (34 * Math.PI) / 180; // ~10.5 bp per turn
    const radius = 1.0; // backbone helix radius
    const L = (nbp - 1) * rise;
    const a: [number, number, number][] = [];
    const b: [number, number, number][] = [];
    for (let i = 0; i < nbp; i++) {
      const y = i * rise - L / 2;
      const th = i * twist;
      a.push([radius * Math.cos(th), y, radius * Math.sin(th)]);
      // Strand B runs antiparallel, half a turn out of phase, so the rungs
      // cross the helix axis like the rungs of a twisted ladder.
      b.push([radius * Math.cos(th + Math.PI), y, radius * Math.sin(th + Math.PI)]);
    }
    // A short deterministic base sequence (indices into the A/T/G/C palette)
    // so the rungs aren't monochrome — no Math.random (unavailable here).
    const seq = [0, 2, 1, 3, 0, 3, 2, 1, 0, 2, 3, 1, 0];
    const rungList = a.map((_, i) => ({ i, base: seq[i % seq.length] }));
    return { strandA: a, strandB: b, rungs: rungList };
  }, []);

  // Scale so the helix fits the tile camera (half-height L/2 dominates).
  const halfHeight = ((13 - 1) * 0.42) / 2;
  const scale = 1.85 / halfHeight;

  // Base palette: A / T / G / C. Each rung is one Bond split-colored at its
  // midpoint into the two paired bases (A–T, G–C) via fromColor/toColor.
  const palette = ['#e0524d', '#e8c84a', '#4f86e8', '#3fc77a'];
  const complement = (n: number): number => (n === 0 ? 1 : n === 1 ? 0 : n === 2 ? 3 : 2);
  const STRAND_A_COLOR = '#6f9ad8';
  const STRAND_B_COLOR = '#d88f6f';

  return (
    <AutoRotate speed={0.34} axis="y" paused={paused}>
      <group scale={scale}>
        {/* Backbone spheres + connecting bonds for each strand. */}
        {strandA.map((p, i) => (
          <Atom key={`a${i}`} position={p} radius={0.17} color={STRAND_A_COLOR} />
        ))}
        {strandB.map((p, i) => (
          <Atom key={`b${i}`} position={p} radius={0.17} color={STRAND_B_COLOR} />
        ))}
        {strandA.slice(0, -1).map((p, i) => (
          <Bond
            key={`ab${i}`}
            from={p}
            to={strandA[i + 1]}
            radius={0.075}
            fromColor={STRAND_A_COLOR}
            toColor={STRAND_A_COLOR}
          />
        ))}
        {strandB.slice(0, -1).map((p, i) => (
          <Bond
            key={`bb${i}`}
            from={p}
            to={strandB[i + 1]}
            radius={0.075}
            fromColor={STRAND_B_COLOR}
            toColor={STRAND_B_COLOR}
          />
        ))}
        {/* Base-pair rungs — split-colored into the two complementary bases. */}
        {rungs.map(({ i, base }) => (
          <Bond
            key={`r${i}`}
            from={strandA[i]}
            to={strandB[i]}
            radius={0.06}
            fromColor={palette[base]}
            toColor={palette[complement(base)]}
          />
        ))}
      </group>
    </AutoRotate>
  );
});

// --- Nanomaterial Design ---
// A (6,6) armchair carbon nanotube, rolled up here from a graphene strip so
// the landing chunk stays free of the crystal modules: lattice a1 = (√3, 0)·d,
// a2 = (√3/2, 3/2)·d, basis A at 0 and B at (0, d); chiral vector n(a1 + a2)
// of length 3n·d becomes the circumference, the perpendicular translation
// vector (length √3·d) the axis. Bonds join atoms closer than 1.15 d.
export const NanotubeScene = React.memo(({ paused, onReady }: SceneProps) => {
  useEffect(() => {
    onReady?.();
  }, [onReady]);
  const { atoms, bonds } = useMemo(() => {
    const n = 6;
    const d = 0.42;
    const periods = 4;
    const a1 = [Math.sqrt(3) * d, 0];
    const a2 = [(Math.sqrt(3) / 2) * d, 1.5 * d];
    const ch = [n * (a1[0] + a2[0]), n * (a1[1] + a2[1])];
    const chLen = Math.hypot(ch[0], ch[1]);
    const chHat = [ch[0] / chLen, ch[1] / chLen];
    const tHat = [-chHat[1], chHat[0]];
    const length = periods * Math.sqrt(3) * d;
    const radius = chLen / (2 * Math.PI);
    const pts: [number, number, number][] = [];
    const seen = new Set<string>();
    for (let i = -3 * n; i <= 3 * n; i++) {
      for (let j = -3 * n; j <= 3 * n; j++) {
        for (const basis of [
          [0, 0],
          [0, d],
        ]) {
          const x = i * a1[0] + j * a2[0] + basis[0];
          const y = i * a1[1] + j * a2[1] + basis[1];
          const u = x * chHat[0] + y * chHat[1];
          const v = x * tHat[0] + y * tHat[1];
          if (u < -1e-6 || u >= chLen - 1e-6 || v < -1e-6 || v >= length - 1e-6) continue;
          const theta = (2 * Math.PI * u) / chLen;
          const p: [number, number, number] = [radius * Math.cos(theta), v - length / 2, radius * Math.sin(theta)];
          const key = p.map((c) => c.toFixed(3)).join(',');
          if (seen.has(key)) continue;
          seen.add(key);
          pts.push(p);
        }
      }
    }
    const b: { from: [number, number, number]; to: [number, number, number] }[] = [];
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i][0] - pts[j][0];
        const dy = pts[i][1] - pts[j][1];
        const dz = pts[i][2] - pts[j][2];
        if (dx * dx + dy * dy + dz * dz < 1.15 * 1.15 * d * d) b.push({ from: pts[i], to: pts[j] });
      }
    }
    return { atoms: pts, bonds: b };
  }, []);
  const carbon = colorFor('C');
  return (
    <AutoRotate speed={0.3} axis="xy" paused={paused}>
      <group rotation={[0, 0, Math.PI / 5]}>
        {atoms.map((p, i) => (
          <Atom key={i} position={p} radius={0.11} color={carbon} />
        ))}
        {bonds.map((bd, i) => (
          <Bond key={i} from={bd.from} to={bd.to} radius={0.035} fromColor={carbon} toColor={carbon} />
        ))}
      </group>
    </AutoRotate>
  );
});
